import { ButtonV2, Checkbox, TooltipTrigger } from 'components';
import { CORP_PERMISSIONS, PERMISSIONS, SCHOOL_PERMISSIONS, getPermissionMap } from 'constants/permissions';
import useLaunchDarkly from 'hooks/useLaunchDarkly';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import StaffSelectionList from './StaffSelectionList';
import './style.scss';

const pickSingleLevel = val => {
  if (!Array.isArray(val)) return val;
  if (val.includes('write')) return 'write';
  if (val.includes('read')) return 'read';
  return 'none';
};

const AssignRoleStaff = ({ onClose, currentSchool, corpSchool, currentUser, tenant, data, allStaff }) => {
  const flags = useLaunchDarkly();
  const initialRole = data.permissionRole || {};

  const [staffIdentifiers, setStaffIdentifiers] = useState(initialRole.pom_staff_identifiers || []);
  const [roleData, setRoleData] = useState(initialRole);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [isAdmin, setIsAdmin] = useState(false);

  const permissionMapWithOverrides = getPermissionMap(flags);
  const ALL_PERMISSIONS = useMemo(() => {
    return (corpSchool ? CORP_PERMISSIONS : PERMISSIONS)
      .filter(key => {
        const hideFlag = permissionMapWithOverrides[key].showPermission;
        return !hideFlag || flags[hideFlag];
      })
      .sort((a, b) => permissionMapWithOverrides[a].label.localeCompare(permissionMapWithOverrides[b].label));
  }, [corpSchool, flags, permissionMapWithOverrides]);

  const clearPermissions = () => {
    setPermissions({});
    setIsAdmin(false);
  };

  const fetchRoleById = useCallback(
    permissionId => {
      clearPermissions();
      const schoolId = currentUser?.current_school?.id;
      if (!schoolId || !tenant?.sites?.length) return Promise.resolve();

      const matchingSite = tenant.sites.find(site => site.pom_identifier === `PCO:SITE:${schoolId}`);
      if (!matchingSite) return Promise.resolve();

      return req
        .fetchRoleById({ site_id: matchingSite.id, permission_role_id: permissionId })
        .then(result => {
          setRoleData(result);
          return result;
        })
        .catch(err => {
          console.error('Error fetching role:', err);
          return null;
        });
    },
    [currentUser, tenant]
  );

  useEffect(() => {
    if (initialRole.id) fetchRoleById(initialRole.id);
  }, [initialRole.id, fetchRoleById]);

  useEffect(() => {
    setStaffIdentifiers(roleData.pom_staff_identifiers || []);
  }, [roleData.pom_staff_identifiers]);

  useEffect(() => {
    setName(roleData.name || '');
    setIsAdmin(!!roleData.is_admin);
    const initPerms = {};
    ALL_PERMISSIONS.forEach(key => {
      const arr = roleData.line_items?.[key] || [];
      initPerms[key] = pickSingleLevel(arr);
    });
    setPermissions(initPerms);
  }, [roleData]);

  const assignStaff = useCallback(() => {
    setSaving(true);
    const schoolId = currentUser?.current_school?.id;
    if (!schoolId || !tenant?.sites?.length) return;

    const matchingSite = tenant.sites.find(site => site.pom_identifier === `PCO:SITE:${schoolId}`);
    if (!matchingSite) return;

    const payload = {
      assignments: staffIdentifiers.map(id => ({ pom_staff_identifier: id }))
    };

    req
      .assignStaffToRole({
        site_id: matchingSite.id,
        permission_role_id: roleData.id,
        ...payload
      })
      .then(() => {
        onClose();
        const count = staffIdentifiers.length;
        const roleName = name?.trim() || 'Role';
        Actions.showFlash(`${count} ${roleName}${count === 1 ? '' : 's'} associated`);
      })
      .catch(err => {
        Actions.reportError('Unable to assign staff to role: ', err);
        setSaving(false);
      });
  }, [staffIdentifiers, currentUser, tenant, roleData.id, onClose]);

  const filteredPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      !SCHOOL_PERMISSIONS.includes(key)
  );
  const schoolPermissions = ALL_PERMISSIONS.filter(
    key =>
      (!permissionMapWithOverrides[key].enabledBySchoolSetting ||
        permissionMapWithOverrides[key].enabledBySchoolSetting(currentSchool)) &&
      SCHOOL_PERMISSIONS.includes(key)
  );

  const renderRow = key => {
    const value = permissions[key];
    const { label, tooltip } = permissionMapWithOverrides[key];
    return (
      <div key={key} className="assign-staff__row">
        <div className="assign-staff__row-label">
          {label}
          {tooltip && (
            <TooltipTrigger tooltip={tooltip} side="center" className="assign-staff__row-tooltip">
              <ButtonV2 icon iconName="tooltip" iconSize={16} />
            </TooltipTrigger>
          )}
        </div>
        <div className="assign-staff__row-values">
          <Checkbox checked={value === 'none'} disabled label="No Access" />
          <Checkbox checked={value === 'read'} disabled label="Read" />
          <Checkbox checked={value === 'write'} disabled label="Full Access" />
        </div>
      </div>
    );
  };

  const hasStaffChanged = useMemo(() => {
    const original = (roleData.pom_staff_identifiers || []).slice().sort();
    const current = staffIdentifiers.slice().sort();

    if (original.length !== current.length) return true;
    return !original.every((id, i) => id === current[i]);
  }, [roleData.pom_staff_identifiers, staffIdentifiers]);

  const isSubmitDisabled = saving || !hasStaffChanged;

  return (
    <div className="modal__wrapper assign-staff">
      <div className="modal__header modal__header--bordered">
        <div className="modal__header-title">Add/Edit Associated Staff</div>
      </div>
      <div className="modal__container">
        <div className="assign-staff__content">
          <div className="assign-staff__role-name-field">
            <div className="role-label">Role Title</div>
            <div className="role-name">{name}</div>
          </div>
          <div className="assign-staff__assignment">
            <div className="assign-staff__assignment-permissions">
              <div className="assign-staff__section">
                {corpSchool && <div className="assign-staff__section-title">Corporate Portal</div>}
                {filteredPermissions.map(renderRow)}
              </div>
              {corpSchool && (
                <div className="assign-staff__section">
                  <div className="assign-staff__section-title">School Permission</div>
                  {schoolPermissions.map(renderRow)}
                </div>
              )}
            </div>
            <div className="assign-staff__assignment-selection">
              <StaffSelectionList
                assignedStaff={staffIdentifiers}
                initialAssignedStaff={initialRole.pom_staff_identifiers}
                onChange={setStaffIdentifiers}
                isDefaultRole={!!roleData.is_default_for_site}
              />
            </div>
          </div>
        </div>
        <div className="form__row form__row--actions">
          <ButtonV2 label="Save" onClick={assignStaff} data-cy="save" isLoading={saving} disabled={isSubmitDisabled} />
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  currentSchool: state.currentUser.data.current_school,
  corpSchool: state.currentUser.data.current_school.corp_school,
  currentUser: state.currentUser.data,
  tenant: state.tenant.data,
  allStaff: state.staff.data
});

export default connect(mapStateToProps)(AssignRoleStaff);
